import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { colorMap } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { NodeViewMode } from '@/enums/node-view-mode.enum';
import { useDeleteAssetsSubMenu } from '@/hooks/useDeleteAssetsSubMenu';
import { useInteractiveDownloader } from '@/hooks/useInteractiveDownloader';
import { useWorkflowStore, type ModelPricesMaps } from '@/state/workflow.state';
import { ModelAssetToUploadedAsset } from '@/types/api/assets';
import { FF_PACK_UNPACK_NODE } from '@/consts/featureFlags';
import { getSafeExternalUrl } from '@/utils/urls';
import { getResizableDimensions } from '@/consts/node-ui.consts';
import { useModelBaseView, useNodeOriginalName } from '../Recipe/FlowContext';
import ModelBaseComponent from './ModelBaseComponent';
import { DynamicNode2 } from './DynamicNode/DynamicNode2';
import { useCreateIteratorFromNode, getIteratorInfo } from './useCreateIteratorFromNode';
import { useUnpackNode } from './useUnpackNode';
import { getModelPriceInfo } from './Utils';
import { getNodeIcon } from './utils/NodeIcon';
import { ModelDetails } from './ModelComponents/ModelDetails';
import type { PackUnpackItem } from './packUnpack.consts';
import type { AiModelData, UpdateNodeData } from '@/types/nodes/model';
import type { MenuAction } from '../Menu/Actions';

interface ModelBaseNodeProps {
  data: AiModelData;
  editable: boolean;
  id: string;
  isSelected: boolean;
  modelPricesMaps: ModelPricesMaps;
  recipeId: string;
  recipeVersion: number;
  updateNodeData: UpdateNodeData;
}

const MODEL_BASE_RESIZABLE_DIMENSIONS = getResizableDimensions(130, 'large');

const DIVIDER_MENU_ACTION: MenuAction = {
  type: 'divider',
};

function ModelBaseNode({
  data,
  editable,
  id,
  isSelected,
  modelPricesMaps,
  recipeId: _recipeId,
  updateNodeData,
}: ModelBaseNodeProps) {
  const { t } = useTranslation();
  const { viewMode = NodeViewMode.Single, kind: modelKind } = data;
  const modelBaseView = useModelBaseView(id);
  const isUnpackEnabled = useFeatureFlagEnabled(FF_PACK_UNPACK_NODE);
  const selectedIndex = data.selectedIndex;
  const generations = useMemo(() => {
    return data.generations?.map(ModelAssetToUploadedAsset) ?? [];
  }, [data.generations]);
  const displayViewMode = generations.length === 1 ? NodeViewMode.Single : viewMode;
  const selectedGeneration = generations[selectedIndex];
  const showFooterSeparator =
    displayViewMode === NodeViewMode.Single &&
    (selectedGeneration?.type === 'video' || selectedGeneration?.type === 'audio');
  const recipeName = useWorkflowStore((state) => state.recipe?.name);
  const workspaceFalByokEnabled = useWorkflowStore((state) => state.workspaceFalByokEnabled);
  const modelPriceLookupOptions = useMemo(() => ({ workspaceFalByokEnabled }), [workspaceFalByokEnabled]);
  const { downloadAssetFromNode, downloadAssetsFromNodeWithProgress } = useInteractiveDownloader();
  const storedOriginalName = useNodeOriginalName(id);
  const safeSourceUrl = useMemo(() => getSafeExternalUrl(data.source), [data.source]);

  const goToSource = useCallback(() => {
    if (safeSourceUrl) {
      window.open(safeSourceUrl, '_blank');
    }
  }, [safeSourceUrl]);

  const setSelectedOutput = useCallback(
    (selectedOrUpdater: number | ((selected: number) => number)) => {
      const newSelectedOutput =
        typeof selectedOrUpdater === 'function' ? selectedOrUpdater(selectedIndex) : selectedOrUpdater;
      modelBaseView.setSelectedIndex(newSelectedOutput);
    },
    [modelBaseView, selectedIndex],
  );

  const downloadCurrentResult = useCallback(async () => {
    const asset = modelBaseView.getCurrentDownloadableAsset();
    if (!asset) {
      return;
    }

    await downloadAssetFromNode({
      url: asset.url,
      fileType: asset.type,
      nodeName: data.name || id,
      prefix: recipeName,
    });
  }, [modelBaseView, data.name, id, recipeName, downloadAssetFromNode]);

  const downloadAllResults = useCallback(async () => {
    const assets = modelBaseView.getAllDownloadableAssets();
    await downloadAssetsFromNodeWithProgress(assets, data.name || id, recipeName, undefined);
  }, [modelBaseView, data.name, id, recipeName, downloadAssetsFromNodeWithProgress]);

  const { unpackNode } = useUnpackNode();

  const unpackItems = useMemo<PackUnpackItem[]>(() => {
    return (data.generations ?? []).map((gen): PackUnpackItem => {
      if (gen.kind.type === 'text') {
        return { type: 'text', text: gen.kind.value };
      }
      return { type: 'file', file: gen.kind };
    });
  }, [data.generations]);

  const handleUnpack = useCallback(() => {
    unpackNode(id, unpackItems);
  }, [unpackNode, id, unpackItems]);

  const unpackMenuItems = useMemo<MenuAction[]>(() => {
    if (!isUnpackEnabled) return [];
    return [
      { type: 'divider' },
      {
        name: t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.UNPACK),
        action: handleUnpack,
        disabled: unpackItems.length <= 1,
      },
    ];
  }, [isUnpackEnabled, unpackItems.length, handleUnpack, t]);

  const { createIterator } = useCreateIteratorFromNode();

  const iteratorInfo = useMemo(() => getIteratorInfo(unpackItems), [unpackItems]);

  const handleCreateIterator = useCallback(() => {
    createIterator(id, unpackItems);
  }, [createIterator, id, unpackItems]);

  const createIteratorMenuItems = useMemo<MenuAction[]>(() => {
    if (!isUnpackEnabled) return [];
    return [
      {
        name: t(iteratorInfo?.labelKey ?? I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.CREATE_ITERATOR),
        action: handleCreateIterator,
        disabled: !iteratorInfo,
      },
    ];
  }, [isUnpackEnabled, iteratorInfo, handleCreateIterator, t]);

  const downloadMenuActions = useMemo<MenuAction[]>(
    () =>
      [
        ...(generations?.length > 1 && generations.at(0)?.type === 'text'
          ? []
          : ([
              DIVIDER_MENU_ACTION,
              {
                name: t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.DOWNLOAD_CURRENT),
                action: downloadCurrentResult,
                disabled: !selectedGeneration,
              },
              {
                name: t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.DOWNLOAD_ALL),
                action: downloadAllResults,
                disabled: !generations || generations.length === 0,
              },
            ] as const)),
      ].filter((item) => !!item),
    [generations, downloadAllResults, downloadCurrentResult, t, selectedGeneration],
  );

  const deleteAssetsMenuItems = useDeleteAssetsSubMenu({
    nodeId: id,
    view: {
      deleteAll: () => modelBaseView.deleteAllResults(),
      deleteAllOthers: () => modelBaseView.deleteAllOtherResults(),
      deleteCurrent: () => modelBaseView.deleteResult(),
      assets: generations,
    },
    texts: {
      deleteCurrentAction: t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.DELETE_CURRENT_GENERATION),
      deleteAllOthersAction: t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.DELETE_ALL_OTHERS_GENERATIONS),
      deleteAllAction: t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.DELETE_ALL_GENERATIONS),
      confirmationMessage: t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU_CONFIRMATION_DIALOG.MESSAGE),
    },
  });

  const nodeMenu = useMemo<MenuAction[]>(
    () =>
      [
        ...downloadMenuActions,
        ...deleteAssetsMenuItems,
        ...unpackMenuItems,
        ...createIteratorMenuItems,
        safeSourceUrl ? DIVIDER_MENU_ACTION : undefined,
        safeSourceUrl
          ? {
              name: t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.LEARN_MORE_ABOUT_THIS_MODEL),
              action: goToSource,
            }
          : undefined,
      ].filter((item) => item !== undefined),
    [
      downloadMenuActions,
      t,
      safeSourceUrl,
      goToSource,
      deleteAssetsMenuItems,
      unpackMenuItems,
      createIteratorMenuItems,
    ],
  );

  const isImportModelNode = useMemo(() => {
    if (editable) {
      // Old data, this variable is true if it is a wildcard node, ie. node.type === 'wildcardV2'.
      return true;
    }
    if (modelKind?.type === 'wildcard') {
      // This is an import model node if there's no model or the model type is imported.
      return modelKind.model.type === 'none' || modelKind.model.type === 'imported';
    }
    return false;
  }, [editable, modelKind]);

  const hasValidModel = useMemo(() => {
    return Boolean(data.model?.name && data.model?.version && data.model?.service) || !isImportModelNode;
  }, [data.model, isImportModelNode]);

  const modelPriceEntry = useMemo(() => {
    if (!data.model) {
      return undefined;
    }
    return getModelPriceInfo(data.model, modelPricesMaps, data.params, modelPriceLookupOptions);
  }, [data.model, data.params, modelPricesMaps, modelPriceLookupOptions]);

  const isModelByok = modelPriceEntry?.isByok === true;

  const modelPrice = useMemo(() => {
    if (!data.model) {
      return undefined;
    }
    return modelPriceEntry?.credits;
  }, [data.model, modelPriceEntry?.credits]);

  const isResizableKind =
    modelKind?.type === 'any_llm' ||
    modelKind?.type === 'image_describer' ||
    modelKind?.type === 'prompt_enhancer' ||
    data.model?.name === 'fal-ai/video-understanding' ||
    data.model?.name === 'openrouter/router/video/enterprise';

  return (
    <>
      <DynamicNode2
        id={id}
        data={data}
        className="model"
        handleColor={colorMap.get(data.color)}
        isResizable={isResizableKind}
        resizableDimensions={MODEL_BASE_RESIZABLE_DIMENSIONS}
        additionalMenu={nodeMenu}
        overrideShowFullNode={!hasValidModel}
        hideHandles={!hasValidModel}
        icon={getNodeIcon(data.model)}
        menuHeader={
          data.model?.name && (
            <ModelDetails
              modelName={storedOriginalName || data.model.name}
              modelType={data.model.name}
              description={data.description}
              price={modelPrice}
              isByok={isModelByok}
            />
          )
        }
      >
        <ModelBaseComponent
          data={data}
          isImportModelNode={isImportModelNode}
          isResizable={isResizableKind}
          hasValidModel={hasValidModel}
          id={id}
          isSelected={isSelected}
          modelPrice={modelPrice}
          selectedOutput={selectedIndex}
          setSelectedOutput={setSelectedOutput}
          showFooterSeparator={showFooterSeparator}
          updateNodeData={updateNodeData}
          viewMode={displayViewMode}
          selectedViewMode={viewMode}
          additionalContextMenuItems={unpackMenuItems}
        />
      </DynamicNode2>
    </>
  );
}

export default ModelBaseNode;
